import { UserAvatar } from '@mxa-argos/components/custom/user-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@mxa-argos/components/shadcn/dropdown-menu';
import { SidebarMenuButton } from '@mxa-argos/components/shadcn/sidebar';
import { Switch } from '@mxa-argos/components/shadcn/switch';
import { useDeveloperMode } from '@mxa-argos/core/hooks/use-developer-mode';
import type { UserIdentity } from '@mxa-argos/core/interfaces/user-identity.interface';
import { forceConfetti } from '@mxa-argos/lib/confetti';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { Check, Code, Copy, Ellipsis, LogOut, PartyPopper } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const SidebarProfileMenu: React.FC = () => {
  const { t } = useTranslation();
  const { data: user } = useGetIdentity<UserIdentity>();
  const { mutate: logout } = useLogout();
  const { isDeveloperMode, toggleDeveloperMode, ready: isDeveloperModeReady } = useDeveloperMode();
  const [copied, setCopied] = useState(false);

  const firstName = user?.name?.split(' ')[0] ?? t('nav.user');

  const handleCopyUid = async () => {
    if (!user?.id) return;
    await navigator.clipboard.writeText(String(user.id));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton className="group-data-[collapsible=icon]:!size-12 group-data-[collapsible=icon]:justify-center">
          <UserAvatar name={firstName} pictureURL={user?.avatar} size="xl" />
          <div className="flex flex-col items-start text-sm overflow-hidden group-data-[collapsible=icon]:hidden">
            <span className="font-medium">{firstName}</span>
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              {user?.email}
            </span>
          </div>
          <Ellipsis className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">UID</span>
            <button
              onClick={handleCopyUid}
              aria-label={t('common.copyUid')}
              className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-mono bg-muted hover:bg-accent transition-colors"
            >
              <span className="truncate max-w-[140px]">{user?.id}</span>
              {copied ? (
                <Check className="size-3 text-mxa-green" />
              ) : (
                <Copy className="size-3 text-muted-foreground" />
              )}
            </button>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => forceConfetti()}>
          <PartyPopper className="mr-2 size-4" />
          Maximilianear
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(event) => event.preventDefault()}
          className="flex items-center"
        >
          <Code className="mr-2 size-4" />
          <span className="flex-1">{t('nav.developerMode')}</span>
          <Switch
            checked={isDeveloperMode}
            disabled={!isDeveloperModeReady}
            onCheckedChange={toggleDeveloperMode}
            aria-label={t('nav.developerMode')}
          />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => logout()} className="text-destructive">
          <LogOut className="mr-2 size-4" />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
